import React, { ReactElement, useRef, useState } from 'react';
import Captcha, { CaptchaHandle } from 'components/Captcha';
import BACKEND_URL from 'constants/backendUrl';
import TURNSTILE_SITE_KEY from 'constants/turnstileSiteKey';
import { Civility } from 'core/enums/civility';
import { POST } from 'utils/http';
import { signUpErrorFor, SignUpError } from './signUpErrors';
import { SCaptcha } from './styles';

export type SignUpInputs = {
    civility?: Civility;
    email: string;
    firstName: string;
    lastName: string;
    origin?: string;
};

/**
 * Ce qui empêche l'envoi avant même d'atteindre le serveur, en plus de ce qu'il peut répondre.
 */
export type SignUpFailure = SignUpError | 'captcha-pending' | 'captcha-unavailable';

type SignUpRequest = {
    /** Le widget à placer dans l'écran, absent quand aucune clé Turnstile n'est configurée. */
    captcha: ReactElement | null;
    /** L'échec à afficher au-dessus du bouton ; l'adresse refusée, elle, revient à l'appelant. */
    failure: SignUpFailure | null;
    /** Renvoie l'échec, ou null si le serveur a accepté la demande. */
    send(inputs: SignUpInputs): Promise<SignUpFailure | null>;
};

/**
 * Une demande d'inscription accompagnée de son captcha. Partagée entre le formulaire et le renvoi
 * de l'email d'activation, qui n'est rien d'autre qu'une nouvelle demande pour la même adresse.
 */
const useSignUpRequest = (): SignUpRequest => {
    const captchaRef = useRef<CaptchaHandle>(null);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [failure, setFailure] = useState<SignUpFailure | null>(null);

    const onCaptchaTokenChange = (token: string | null) => {
        setCaptchaToken(token);
        if (token) {
            setFailure((current) => (current?.startsWith('captcha-') ? null : current));
        }
    };

    const send = async (inputs: SignUpInputs): Promise<SignUpFailure | null> => {
        if (TURNSTILE_SITE_KEY && !captchaToken) {
            setFailure('captcha-pending');
            return 'captcha-pending';
        }

        setFailure(null);
        try {
            await POST(`${BACKEND_URL}/api/users/sign-up`, {
                ...inputs,
                ...(captchaToken ? { captchaToken } : {}),
            });

            return null;
        } catch (error) {
            const signUpError = signUpErrorFor(error);
            setFailure('invalid-email' === signUpError ? null : signUpError);

            return signUpError;
        } finally {
            // Le jeton a été consommé par le serveur, quelle que soit l'issue.
            captchaRef.current?.reset();
        }
    };

    const captcha = TURNSTILE_SITE_KEY ? (
        <SCaptcha>
            <Captcha
                ref={captchaRef}
                onError={() => setFailure('captcha-unavailable')}
                onTokenChange={onCaptchaTokenChange}
                siteKey={TURNSTILE_SITE_KEY}
            />
        </SCaptcha>
    ) : null;

    return { captcha, failure, send };
};

export default useSignUpRequest;
